/** CloudFront WAF SizeRestrictions_BODY is 8KB. Stay under that per PATCH. */
const WAF_MAX_BODY_BYTES = 7500;

const SOLO_KEYS = [
	"packages",
	"media",
	"addons",
	"detailsHtml",
	"detailsJson",
	"countdownOffer",
	"listingAiContextSummary",
] as const;

/** Large array fields use `{ mediaPacked: "gz:…" }` instead of raw arrays when over the WAF limit. */
const PACKED_ARRAY_KEYS: Record<(typeof SOLO_KEYS)[number], string> = {
	packages: "packagesPacked",
	media: "mediaPacked",
	addons: "addonsPacked",
	detailsHtml: "",
	detailsJson: "",
	countdownOffer: "",
	listingAiContextSummary: "",
};

const WAF_HTML_RE = /style\s*=|on\w+\s*=|<script|<iframe|javascript:/i;

function utf8ToBase64(value: string): string {
	const bytes = new TextEncoder().encode(value);
	let binary = "";
	for (let i = 0; i < bytes.length; i++) {
		binary += String.fromCharCode(bytes[i]!);
	}
	return btoa(binary);
}

async function gzipToBase64(value: string): Promise<string> {
	const stream = new Blob([value]).stream().pipeThrough(new CompressionStream("gzip"));
	const buffer = await new Response(stream).arrayBuffer();
	const bytes = new Uint8Array(buffer);
	let binary = "";
	for (let i = 0; i < bytes.length; i++) {
		binary += String.fromCharCode(bytes[i]!);
	}
	return btoa(binary);
}

function jsonBytes(value: unknown): number {
	return new TextEncoder().encode(JSON.stringify(value)).length;
}

async function packStringField(value: string): Promise<string> {
	if (!WAF_HTML_RE.test(value) && jsonBytes({ v: value }) < WAF_MAX_BODY_BYTES) {
		return value;
	}
	const b64 = `b64:${utf8ToBase64(value)}`;
	if (jsonBytes({ v: b64 }) < WAF_MAX_BODY_BYTES) return b64;
	return `gz:${await gzipToBase64(value)}`;
}

async function packSoloChunk(key: (typeof SOLO_KEYS)[number], value: unknown): Promise<Record<string, unknown>> {
	const rawChunk = { [key]: value };
	if (jsonBytes(rawChunk) < WAF_MAX_BODY_BYTES) {
		return rawChunk;
	}

	const packedKey = PACKED_ARRAY_KEYS[key];
	if (packedKey) {
		const packed = await packStringField(JSON.stringify(value));
		return { [packedKey]: packed };
	}

	if (typeof value === "string") {
		return { [key]: await packStringField(value) };
	}

	return rawChunk;
}

/**
 * Split a service PATCH so CloudFront WAF does not 403 (8KB body / HTML XSS rules).
 */
export async function buildWafSafeServicePatchChunks(
	body: Record<string, unknown>,
): Promise<Record<string, unknown>[]> {
	const prepared: Record<string, unknown> = { ...body };

	if (typeof prepared.detailsHtml === "string") {
		prepared.detailsJson = "";
		prepared.detailsHtml = await packStringField(prepared.detailsHtml);
	} else if (typeof prepared.detailsJson === "string") {
		prepared.detailsJson = await packStringField(prepared.detailsJson);
	}

	const rest: Record<string, unknown> = {};
	const chunks: Record<string, unknown>[] = [];

	for (const [key, value] of Object.entries(prepared)) {
		if (value === undefined) continue;
		if ((SOLO_KEYS as readonly string[]).includes(key)) {
			chunks.push(await packSoloChunk(key as (typeof SOLO_KEYS)[number], value));
		} else {
			rest[key] = value;
		}
	}

	if (Object.keys(rest).length > 0) chunks.unshift(rest);
	return chunks.filter((chunk) => Object.keys(chunk).length > 0);
}
